import {keys} from 'lodash';
import {constant} from 'lodash/fp';

import {PULL_TO_REFRESH_TIME_COUNT} from '/constant/time.constant';
import {CANDIDATE_NOTES_STATE_IS_LOADING} from '/enum/candidates.enum';
import {
  FAILED_TO_PROCESS_REQUEST_EXCEPTION,
  NOT_AUTHORIZED_EXCEPTION,
  SERVER_IS_DOWN_EXCEPTION,
} from '/enum/errors.enum';
import {
  REPORT_ALL_CANDIDATES,
  REPORT_ALL_FRONTLINE_CLIENTS,
  REPORT_ALL_GEN0_CLIENTS,
  REPORT_ALL_ORGANIZATION_CLIENTS,
  REPORT_ALL_REFERRALS,
  REPORT_CLIENT_TO_CONTACT_NEW_CANDIDATE,
  REPORT_NEW_FRONTLINE_CLIENTS,
  REPORT_NEW_GEN0_CLIENTS,
  REPORT_NEW_ORGANIZATION_CLIENTS,
} from '/enum/records.enum';
import {
  CANDIDATES,
  DELETE_CANDIDATE_REMINDER,
  FETCH_CANDIDATE,
  FETCH_CANDIDATE_DECLINE_REASONS,
  FETCH_CANDIDATE_DISPOSITION_OPTIONS,
  FETCH_MERGECANDIDATE,
  POST_CANDIDATE_REMINDER,
  REQUEST_POST,
  REQUEST_PUT,
  UPDATE_CANDIDATE_REMINDER,
} from '/enum/request.enum';
import {ACTION_TYPES} from '/enum/tealium.enum';
import {getClientMetrics} from '/redux/action/metric.action';
import {request} from '/redux/api/request';
import signOut from '/redux/reducer/candidate/auth/signOut';
import {createPromisedAction} from '/util/action.util';
import {delayFor} from '/util/timer.util';

import {displayApiError} from './apiError.action';
import {getErrorHandler} from './apiError.action';
import {resetReport} from './report.action';
import {resetClientProfile} from './sharedCoachClient.action';

export const GET_PROFILE: string = 'CANDIDATE/GET_PROFILE';
export const UPDATE_CANDIDATE_INFO: string = 'CANDIDATE/UPDATE_CANDIDATE_INFO';
export const RESET_CANDIDATE_INFO: string = 'RESET_CANDIDATE_INFO';
export const RESET_ALL_CANDIDATES_INFO: string = 'RESET_ALL_CANDIDATES_INFO';
export const SET_CANDIDATE_NOTES_STATE: string = 'SET_CANDIDATE_NOTES_STATE';
export const COACH_CANDIDATE: string = 'COACH_CANDIDATE';
export const EDIT_CANDIDATE_INFO: string = 'EDIT_CANDIDATE_INFO';
export const UPDATE_CANDIDATE_STATUS: string =
  'CANDIDATE/UPDATE_CANDIDATE_STATUS';
export const UPDATE_CANDIDATE_DISPOSITION: string =
  'CANDIDATE/UPDATE_CANDIDATE_DISPOSITION';
export const GET_CANDIDATE_DECLINE_REASONS: string =
  'CANDIDATE/GET_CANDIDATE_DECLINE_REASONS';
export const GET_CANDIDATE_DISPOSITION_OPTIONS: string =
  'CANDIDATE/GET_CANDIDATE_DISPOSITION_OPTIONS';
export const DELETE_PROFILE: string = 'CANDIDATE/DELETE_PROFILE';
export const MERGE_PROFILE: string = 'CANDIDATE/MERGE_PROFILE';
export const GET_REFRESHED_PROFILE: string = 'CANDIDATE/GET_REFRESHED_PROFILE';
export const SAVE_CANDIDATE_REMINDER: string =
  'CANDIDATE/SAVE_CANDIDATE_REMINDER';
export const DELETE_REMINDER: string = 'CANDIDATE/DELETE_REMINDER';
export const UPDATE_REMINDER: string = 'CANDIDATE/UPDATE_REMINDER';
export const GET_ALL_REFERRALS: string = 'CANDIDATE/GET_ALL_REFERRALS';
export const RESET_ALL_REFERRAL_INFO: string = 'RESET_ALL_REFERRAL_INFO';

export const getProfile = createPromisedAction(
  GET_PROFILE,
  ['candidateId'],
  (dispatchMe: any, inputPayload?: any, dispatch?: any) => {
    const {candidateId} = inputPayload;
    return request(FETCH_CANDIDATE, {
      URLParams: candidateId,
    })
      .then(candidate => {
        dispatchMe(() => ({candidateId, candidate}));
      })
      .catch(
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.

        getErrorHandler(dispatch, {
          promisedAction: getProfile,
          params: inputPayload,
        }),
      );
  },
);

//Using a separate component for refresh to apply a delay for better animation
export const getRefreshedProfile = createPromisedAction(
  GET_REFRESHED_PROFILE,
  ['candidateId'],
  (dispatchMe: any, inputPayload?: any, dispatch?: any) => {
    const {candidateId} = inputPayload;
    return delayFor(PULL_TO_REFRESH_TIME_COUNT)
      .then(() => dispatch(getProfile({candidateId})))
      .catch(constant(null));
  },
);

export const resetCandidateInfo = createPromisedAction(RESET_CANDIDATE_INFO, [
  'candidateId',
  'data',
]);
export const resetAllCandidatesInfo = createPromisedAction(
  RESET_ALL_CANDIDATES_INFO,
  [],
);

export const setCandidateNotesState = createPromisedAction(
  SET_CANDIDATE_NOTES_STATE,
  ['candidateId', 'stateName', 'stateValue'],
);
export const updateCandidateNotes = createPromisedAction(
  UPDATE_CANDIDATE_INFO,
  ['candidateId', 'body'],
  (dispatchMe: any, inputPayload?: any, dispatch?: any) => {
    const {candidateId, body} = inputPayload;
    return Promise.resolve()
      .then(() =>
        request(FETCH_CANDIDATE, {
          URLParams: candidateId,
          body: body,
          method: REQUEST_PUT,
        }),
      )
      .then(candidate => {
        dispatchMe(() => ({candidateId, candidate}));
      })
      .catch(
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
        getErrorHandler(dispatch, {
          promisedAction: updateCandidateNotes,
          params: inputPayload,
        }),
      )
      .then(() => {
        // Calling candidate api to reload the updated data
        dispatch(resetCandidateInfo({candidateId, data: null}));
      })
      .then(() =>
        // Finally set the loading state back to false.
        dispatch(
          setCandidateNotesState({
            candidateId,
            stateName: CANDIDATE_NOTES_STATE_IS_LOADING,
            stateValue: false,
          }),
        ),
      )
      .catch(
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
        getErrorHandler(dispatch, {
          promisedAction: resetCandidateInfo,
          params: candidateId,
        }),
      );
  },
);
export const saveCandidateProfile = createPromisedAction(
  COACH_CANDIDATE,
  ['candidateId', 'body', 'allReportData', 'sendEvent'],
  (dispatchMe: any, inputPayload?: any, dispatch?: any) => {
    const {candidateId, body, allReportData, sendEvent} = inputPayload;

    const {
      clientName,
      clientEmailAddress,
      clientPhone,
      urlDetails,
      lastPaidRank,
    } = body || {};
    return Promise.resolve()
      .then(() =>
        request(CANDIDATES, {
          URLParams: candidateId,
          body: body,
          method: REQUEST_POST,
        }),
      )
      .then(candidate => {
        // Check if the response indicates an error
        if (
          candidate &&
          typeof candidate === 'object' &&
          !Array.isArray(candidate) &&
          ((candidate as any)?.statusCode >= 400 || (candidate as any)?.message)
        ) {
          // Throw error for non-success responses
          throw {
            statusCode: (candidate as any).statusCode,
            message: (candidate as any).message || 'API Error',
            response: candidate,
          };
        }

        // @ts-expect-error TS(2339): Property 'prospectInfoId' does not exist on type '... Remove this comment to see the full error message
        const {prospectInfoId} = candidate || {};
        sendEvent({
          userId: prospectInfoId,
          updated_fields: `${clientName},${clientEmailAddress},${clientPhone},${urlDetails}`,
          action: ACTION_TYPES.new,
          lastPaidRank: `${lastPaidRank}`,
        });

        dispatchMe(() => ({candidateId, candidate}));
        return candidate; // Return the successful response
      })
      .then(candidate => {
        const reportsKeys = keys(allReportData);
        const currentKey = REPORT_ALL_CANDIDATES;

        const filteredReportsKeys = reportsKeys.filter((key: any) =>
          key.startsWith(currentKey),
        );
        filteredReportsKeys.forEach((element: any) => {
          dispatch(
            resetReport({
              reportKey: element,
              data: null,
            }),
          );
        });
        return candidate;
      })
      .then(candidate => {
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
        dispatch(getClientMetrics());
        return candidate; // Return the successful response
      })
      .catch(error => {
        // Re-throw the error so it can be caught by the calling function
        throw error;
      });
  },
);
export const saveEditCandidateProfile = createPromisedAction(
  EDIT_CANDIDATE_INFO,
  ['candidateId', 'body', 'allReportData'],
  (dispatchMe: any, inputPayload?: any, dispatch?: any) => {
    const {candidateId, body, allReportData} = inputPayload;
    return Promise.resolve()
      .then(() =>
        request(FETCH_CANDIDATE, {
          URLParams: candidateId,
          body: body,
          method: REQUEST_PUT,
        }),
      )
      .then(candidate => {
        dispatchMe(() => ({candidateId, candidate}));
      })
      .then(() => {
        // Calling candidate api to reload the updated data
        dispatch(resetCandidateInfo({candidateId, data: null}));
      })
      .then(() => {
        const reportsKeys = keys(allReportData);
        const currentKey = REPORT_CLIENT_TO_CONTACT_NEW_CANDIDATE;
        const filteredReportsKeys = reportsKeys.filter((key: any) =>
          key.startsWith(currentKey),
        );
        filteredReportsKeys.forEach((element: any) => {
          dispatch(
            resetReport({
              reportKey: element,
              data: null,
            }),
          );
        });
      })
      .then(() => {
        const reportsKeys = keys(allReportData);
        const currentKey = REPORT_ALL_CANDIDATES;
        const filteredReportsKeys = reportsKeys.filter((key: any) =>
          key.startsWith(currentKey),
        );
        filteredReportsKeys.forEach((element: any) => {
          dispatch(
            resetReport({
              reportKey: element,
              data: null,
            }),
          );
        });
      })
      .catch(
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
        getErrorHandler(dispatch, {
          promisedAction: saveEditCandidateProfile,
          params: inputPayload,
        }),
      );
  },
);
export const deleteCandidate = createPromisedAction(
  DELETE_PROFILE,
  ['candidateId', 'allReportData'],
  (dispatchMe: any, inputPayload?: any, dispatch?: any) => {
    const {candidateId, allReportData} = inputPayload;
    return Promise.resolve()
      .then(() =>
        request(FETCH_CANDIDATE, {
          URLParams: candidateId,
          body: {status: 'R'},
          method: REQUEST_PUT,
        }),
      )
      .then(candidate => {
        dispatchMe(() => ({candidateId, candidate}));
      })
      .then(() => {
        const reportsKeys = keys(allReportData);
        const currentKey = REPORT_CLIENT_TO_CONTACT_NEW_CANDIDATE;
        const filteredReportsKeys = reportsKeys.filter((key: any) =>
          key.startsWith(currentKey),
        );
        filteredReportsKeys.forEach((element: any) => {
          dispatch(
            resetReport({
              reportKey: element,
              data: null,
            }),
          );
        });
      })
      .then(() => {
        const reportsKeys = keys(allReportData);
        const currentKey = REPORT_ALL_CANDIDATES;
        const filteredReportsKeys = reportsKeys.filter((key: any) =>
          key.startsWith(currentKey),
        );
        filteredReportsKeys.forEach((element: any) => {
          dispatch(
            resetReport({
              reportKey: element,
              data: null,
            }),
          );
        });
      })
      .then(() => {
        const reportsKeys = keys(allReportData);
        const currentKey = REPORT_ALL_REFERRALS;
        const filteredReportsKeys = reportsKeys.filter((key: any) =>
          key.startsWith(currentKey),
        );
        filteredReportsKeys.forEach((element: any) => {
          dispatch(
            resetReport({
              reportKey: element,
              data: null,
            }),
          );
        });
      })
      .then(() => {
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
        dispatch(getClientMetrics());
      })
      .catch(
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
        getErrorHandler(dispatch, {
          promisedAction: deleteCandidate,
          params: inputPayload,
        }),
      );
  },
);

export const mergeCandidate = createPromisedAction(
  MERGE_PROFILE,
  ['candidateId', 'body', 'allReportData'],
  (dispatchMe: any, inputPayload?: any, dispatch?: any) => {
    const {candidateId, body, allReportData} = inputPayload;
    return Promise.resolve()
      .then(() =>
        request(FETCH_MERGECANDIDATE, {
          URLParams: candidateId,
          body: body,
          method: REQUEST_PUT,
        }),
      )
      .then(candidate => {
        dispatchMe(() => ({candidateId, candidate}));
      })
      .then(() => {
        const reportsKeys = keys(allReportData);
        const currentKey = REPORT_CLIENT_TO_CONTACT_NEW_CANDIDATE;
        const filteredReportsKeys = reportsKeys.filter((key: any) =>
          key.startsWith(currentKey),
        );
        filteredReportsKeys.forEach((element: any) => {
          dispatch(
            resetReport({
              reportKey: element,
              data: null,
            }),
          );
        });
      })
      .then(() => {
        const reportsKeys = keys(allReportData);
        const currentKey = REPORT_ALL_CANDIDATES;
        const filteredReportsKeys = reportsKeys.filter((key: any) =>
          key.startsWith(currentKey),
        );
        filteredReportsKeys.forEach((element: any) => {
          dispatch(
            resetReport({
              reportKey: element,
              data: null,
            }),
          );
        });
      })
      .then(() => {
        const reportsKeys = keys(allReportData);
        const currentKey =
          REPORT_NEW_FRONTLINE_CLIENTS ||
          REPORT_NEW_GEN0_CLIENTS ||
          REPORT_NEW_ORGANIZATION_CLIENTS;
        const filteredReportsKeys = reportsKeys.filter((key: any) =>
          key.startsWith(currentKey),
        );
        filteredReportsKeys.forEach((element: any) => {
          dispatch(
            resetReport({
              reportKey: element,
              data: null,
            }),
          );
        });
      })
      .then(() => {
        const reportsKeys = keys(allReportData);
        const currentKey =
          REPORT_ALL_FRONTLINE_CLIENTS ||
          REPORT_ALL_GEN0_CLIENTS ||
          REPORT_ALL_ORGANIZATION_CLIENTS;
        const filteredReportsKeys = reportsKeys.filter((key: any) =>
          key.startsWith(currentKey),
        );
        filteredReportsKeys.forEach((element: any) => {
          dispatch(
            resetReport({
              reportKey: element,
              data: null,
            }),
          );
        });
      })
      .then(() => {
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
        dispatch(getClientMetrics());
      })
      .then(() => {
        // Calling client api to reload the updated data
        dispatch(resetClientProfile({clientId: body.clientId}));
      })
      .catch(
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
        getErrorHandler(dispatch, {
          promisedAction: mergeCandidate,
          params: inputPayload,
        }),
      );
  },
);

export const updateCandidateStatus = createPromisedAction(
  UPDATE_CANDIDATE_STATUS,
  ['candidateId', 'body', 'allReportData'],
  (dispatchMe: any, inputPayload?: any, dispatch?: any) => {
    const {candidateId, body, allReportData} = inputPayload;
    return Promise.resolve()
      .then(() =>
        request(FETCH_CANDIDATE, {
          URLParams: candidateId,
          body: body,
          method: REQUEST_PUT,
        }),
      )
      .then(
        () => {
          const reportsKeys = keys(allReportData);
          const currentKey = REPORT_CLIENT_TO_CONTACT_NEW_CANDIDATE;
          const filteredReportsKeys = reportsKeys.filter((key: any) =>
            key.startsWith(currentKey),
          );
          filteredReportsKeys.forEach((element: any) => {
            dispatch(
              resetReport({
                reportKey: element,
                data: null,
              }),
            );
          });
        },
        // need to reset reprot state if the api call has failed as well
        err => {
          const reportsKeys = keys(allReportData);
          const currentKey = REPORT_CLIENT_TO_CONTACT_NEW_CANDIDATE;
          const filteredReportsKeys = reportsKeys.filter((key: any) =>
            key.startsWith(currentKey),
          );
          filteredReportsKeys.forEach((element: any) => {
            dispatch(
              resetReport({
                reportKey: element,
                data: null,
              }),
            );
          });
          const {code: errorCode} = err;
          if (
            errorCode === SERVER_IS_DOWN_EXCEPTION ||
            errorCode === FAILED_TO_PROCESS_REQUEST_EXCEPTION
          ) {
            dispatch(displayApiError({errorCode}));
          } else if (err.code === NOT_AUTHORIZED_EXCEPTION) {
            dispatch(signOut());
          }
        },
      )
      .then(
        () => {
          const reportsKeys = keys(allReportData);
          const currentKey = REPORT_ALL_CANDIDATES;
          const filteredReportsKeys = reportsKeys.filter((key: any) =>
            key.startsWith(currentKey),
          );
          filteredReportsKeys.forEach((element: any) => {
            dispatch(
              resetReport({
                reportKey: element,
                data: null,
              }),
            );
          });
        },
        // need to reset report state if the api call has failed as well
        () => {
          const reportsKeys = keys(allReportData);
          const currentKey = REPORT_ALL_CANDIDATES;
          const filteredReportsKeys = reportsKeys.filter((key: any) =>
            key.startsWith(currentKey),
          );
          filteredReportsKeys.forEach((element: any) => {
            dispatch(
              resetReport({
                reportKey: element,
                data: null,
              }),
            );
          });
        },
      )
      .then(candidate => {
        dispatchMe(() => ({candidateId, candidate}));
      })
      .then(() => {
        // Calling candidate api to reload the updated data
        dispatch(resetCandidateInfo({candidateId, data: null}));
      })

      .then(() => {
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
        dispatch(getClientMetrics());
      })
      .catch(
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
        getErrorHandler(dispatch, {
          promisedAction: saveEditCandidateProfile,
          params: inputPayload,
        }),
      );
  },
);

export const getCandidateDeclineReasons = createPromisedAction(
  GET_CANDIDATE_DECLINE_REASONS,
  [],
  (dispatchMe, inputPayload?: any, dispatch?: any) => {
    return request(FETCH_CANDIDATE_DECLINE_REASONS)
      .then(res => {
        dispatchMe(() => ({res}));
      })
      .catch(
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
        getErrorHandler(dispatch, {
          promisedAction: getCandidateDeclineReasons,
          params: inputPayload,
        }),
      );
  },
);

export const saveCheckInReminder = createPromisedAction(
  SAVE_CANDIDATE_REMINDER,
  ['candidateId', 'body'],
  (dispatchMe: any, inputPayload?: any, dispatch?: any) => {
    const {reminderData, candidateId} = inputPayload;
    return new Promise((resolve, reject) => {
      Promise.resolve()
        .then(() => {
          // Make the request call and return the result
          return request(POST_CANDIDATE_REMINDER, {
            body: reminderData,
            method: REQUEST_POST,
          });
        })
        .then(candidate => {
          dispatchMe(() => ({candidateId, candidate}));
        })
        .then(candidate => {
          // Calling candidate api to reload the updated data
          dispatch(resetCandidateInfo({candidateId, data: null}));
          resolve(candidate);
        })
        .catch(err => {
          // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
          getErrorHandler(dispatch, {
            promisedAction: saveCheckInReminder,
            params: inputPayload,
          })(err);
          reject(err);
        });
    });
  },
);

export const deleteReminder = createPromisedAction(
  DELETE_REMINDER,
  ['candidateId'],
  (dispatchMe: any, inputPayload?: any, dispatch?: any) => {
    const {candidateId} = inputPayload;
    return new Promise((resolve, reject) => {
      Promise.resolve()
        .then(() =>
          request(DELETE_CANDIDATE_REMINDER, {
            URLParams: candidateId,
            body: {status: 'R'},
            method: REQUEST_PUT,
          }),
        )
        .then(candidate => {
          dispatchMe(() => ({candidateId, candidate}));
          dispatch(resetCandidateInfo({candidateId, data: null}));
          resolve(candidate);
        })
        .catch(err => {
          // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
          getErrorHandler(dispatch, {
            promisedAction: saveCheckInReminder,
            params: inputPayload,
          })(err);
          reject(err);
        });
    });
  },
);

export const updateCheckInReminder = createPromisedAction(
  UPDATE_REMINDER,
  ['candidateId', 'body'],
  (dispatchMe: any, inputPayload?: any, dispatch?: any) => {
    const {reminderData, candidateId} = inputPayload;
    return new Promise((resolve, reject) => {
      Promise.resolve()
        .then(() => {
          // Make the request call and return the result
          return request(UPDATE_CANDIDATE_REMINDER, {
            URLParams: candidateId,
            body: reminderData,
            method: REQUEST_PUT,
          });
        })
        .then(candidate => {
          dispatchMe(() => ({candidateId, candidate}));
          dispatch(resetCandidateInfo({candidateId, data: null}));
          resolve(candidate);
        })
        .catch(err => {
          // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
          getErrorHandler(dispatch, {
            promisedAction: updateCheckInReminder,
            params: inputPayload,
          })(err);
          reject(err);
        });
    });
  },
);

export const getDispositionOptions = createPromisedAction(
  GET_CANDIDATE_DISPOSITION_OPTIONS,
  ['type'],
  (dispatchMe: any, inputPayload?: any, dispatch?: any) => {
    return request(FETCH_CANDIDATE_DISPOSITION_OPTIONS, {
      body: {type: 'CANDIDATE_DISPOSITION'},
    })
      .then(res => {
        dispatchMe(() => ({res}));
      })
      .catch(
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
        getErrorHandler(dispatch, {
          promisedAction: getDispositionOptions,
          params: inputPayload,
        }),
      );
  },
);
export const saveDispositionData = createPromisedAction(
  UPDATE_CANDIDATE_DISPOSITION,
  ['candidateId', 'body', 'allReportData'],
  (dispatchMe: any, inputPayload?: any, dispatch?: any) => {
    const {candidateId, body, allReportData} = inputPayload;
    return Promise.resolve()
      .then(() =>
        request(FETCH_CANDIDATE, {
          URLParams: candidateId,
          body: body,
          method: REQUEST_PUT,
        }),
      )
      .then(candidate => {
        dispatchMe(() => ({candidateId, candidate}));
      })
      .then(() => {
        // Calling candidate api to reload the updated data
        dispatch(resetCandidateInfo({candidateId, data: null}));
      })
      .then(() => {
        const reportsKeys = keys(allReportData);
        const currentKey = REPORT_CLIENT_TO_CONTACT_NEW_CANDIDATE;
        const filteredReportsKeys = reportsKeys.filter((key: any) =>
          key.startsWith(currentKey),
        );
        filteredReportsKeys.forEach((element: any) => {
          dispatch(
            resetReport({
              reportKey: element,
              data: null,
            }),
          );
        });
      })
      .then(() => {
        const reportsKeys = keys(allReportData);
        const currentKey = REPORT_ALL_CANDIDATES;
        const filteredReportsKeys = reportsKeys.filter((key: any) =>
          key.startsWith(currentKey),
        );
        filteredReportsKeys.forEach((element: any) => {
          dispatch(
            resetReport({
              reportKey: element,
              data: null,
            }),
          );
        });
      })
      .catch(
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
        getErrorHandler(dispatch, {
          promisedAction: saveDispositionData,
          params: inputPayload,
        }),
      );
  },
);

export const resetAllReferralInfo = createPromisedAction(
  RESET_ALL_REFERRAL_INFO,
  [],
);
